import React, { useState, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import './index.css';
import LandingPage from './components/LandingPage';

// The full App loads lazily — Firebase, Gemini, all itinerary components (~350KB gz)
// won't block the landing page from rendering instantly
const App = lazy(() => import('./App'));

// Branded loading screen shown while App bundle downloads
const AppLoading: React.FC = () => (
  <div className="fixed inset-0 bg-charcoal flex items-center justify-center">
    <div style={{ opacity: 0, animation: 'fadeInUp 400ms cubic-bezier(0.16, 1, 0.3, 1) 50ms forwards' }} className="flex flex-col items-center gap-6">
      <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      <div className="text-center">
        <span className="font-display text-lg tracking-[0.4em] text-ivory uppercase block">Aura Traveler</span>
        <span className="font-serif italic text-xs text-ivory/40 mt-2 block">Preparing your experience...</span>
      </div>
    </div>
    <style>{`@keyframes fadeInUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }`}</style>
  </div>
);

const Shell: React.FC = () => {
  const [showApp, setShowApp] = useState(false);
  const location = useLocation();

  // Every route except the landing page needs the full App. The previous
  // per-route allowlist silently served the LANDING PAGE for any route it
  // missed (/hotel-concierge and /pricing were missing — direct visits and
  // crawlers got the homepage), and unknown URLs never reached the real 404.
  const needsApp = location.pathname !== '/';

  // Preload App bundle after first paint (must be above early return to satisfy Rules of Hooks)
  React.useEffect(() => {
    if (showApp || needsApp) return; // already loading/loaded
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => { import('./App'); });
    } else {
      setTimeout(() => { import('./App'); }, 100);
    }
  }, [showApp, needsApp]);

  if (showApp || needsApp) {
    return (
      <main id="main-content">
        <Suspense fallback={<AppLoading />}>
          <App />
        </Suspense>
      </main>
    );
  }

  // Landing page renders IMMEDIATELY — no Firebase, no heavy bundles
  return <main id="main-content"><LandingPage onEnter={() => setShowApp(true)} /></main>;
};

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <BrowserRouter>
    <Shell />
  </BrowserRouter>
);
